import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  V2Label,
} from "web-design-system";
import { ArrowRightIcon, ExternalLinkIcon, InfoIcon } from "lucide-react";
import { Client, GiftKycAmc } from "creso-core/types";
import { getInitials } from "creso-core/utils";
import { AmcLogo, Avatar, ClientSearchSelect } from "web-core";
import { ACTIVE_STATUSES } from "creso-core/constants";
import { investorNomineesQueryOption } from "creso-core/hooks";
import { useGiftAmcs } from "../../../hooks/use-gift-amcs";
import { StepProps } from "../../../types";

export function ClientSelectStep({ formData, onNext }: StepProps) {
  const giftAmcs = useGiftAmcs();
  const [selectedClient, setSelectedClient] = useState<Client | null>(
    formData.selectedClient
  );
  const [selectedAmc, setSelectedAmc] = useState<GiftKycAmc>(formData.amc);

  const router = useRouter();

  const { data: nominees, isLoading: isLoadingNominees } = useQuery({
    ...investorNomineesQueryOption(selectedClient?.investment_account ?? ""),
  });

  // GIFT onboarding needs PAN/ID, phone and email for every nominee.
  const nomineeWithMissingInfo = useMemo(
    () =>
      nominees?.find(
        (nominee) =>
          !nominee.identity_number || !nominee.mobile || !nominee.email
      ),
    [nominees]
  );

  const openNomineeEdit = () => {
    if (!selectedClient || !nomineeWithMissingInfo) return;
    const { href } = router.buildLocation({
      to: "/clients/$id/account",
      params: { id: String(selectedClient.id) },
      search: { edit_nominee: String(nomineeWithMissingInfo.id) },
    });
    window.open(href, "_blank", "noopener");
  };

  const handleContinue = () => {
    if (!selectedClient) return;
    onNext({ amc: selectedAmc, selectedClient });
  };

  return (
    <div className="flex flex-col h-full px-0.5 overflow-hidden">
      <span className="text-2xl font-bold text-foreground mb-5">
        Select client for{" "}
        <span className="text-gift-foreground">GIFT Outbound</span>
      </span>

      <div className="flex flex-col gap-1.5 mb-5 w-72">
        <V2Label className="font-normal">AMC</V2Label>
        <Select
          value={selectedAmc}
          onValueChange={(value) => setSelectedAmc(value as GiftKycAmc)}
        >
          <SelectTrigger className="h-10">
            <SelectValue placeholder="Select AMC" />
          </SelectTrigger>
          <SelectContent>
            {giftAmcs.map((amc) => {
              const isSelectable = amc.enabled && !!amc.kyc_code;
              return (
                <SelectItem
                  key={amc.ms_code}
                  value={amc.kyc_code ?? amc.ms_code}
                  disabled={!isSelectable}
                >
                  <div className="flex items-center gap-2">
                    <AmcLogo size="xs" amc_code={amc.ms_code} />
                    <span>{amc.name}</span>
                    {!isSelectable && (
                      <Badge
                        variant="custom"
                        className="bg-tag-grey-foreground px-2 py-0.5 font-normal rounded text-tag-grey"
                      >
                        Coming soon
                      </Badge>
                    )}
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-1.5">
        <V2Label className="font-normal">Client</V2Label>
        <ClientSearchSelect
          autoFocus={false}
          onSelect={setSelectedClient}
          clientsFilter={{ status: ACTIVE_STATUSES }}
        />
      </div>

      {/* Selected client info card */}
      {selectedClient && (
        <div className="mt-4 flex flex-col gap-3 rounded-lg border border-border bg-tag-slate p-4">
          <div className="flex items-center gap-3">
            <Avatar
              url={""}
              fallback={getInitials(selectedClient.name)}
              size="md"
            />
            <span className="text-sm font-semibold text-foreground">
              {selectedClient.name}
            </span>
          </div>
          <div className="flex text-sm text-muted-foreground items-center justify-between">
            <span>{selectedClient.pan}</span>
            <span>{selectedClient.email}</span>
            <span>{selectedClient.phone_number}</span>
          </div>
        </div>
      )}

      {selectedClient && nomineeWithMissingInfo && (
        <Alert className="mt-4 bg-tag-blue text-tag-blue-foreground border-none items-center">
          <InfoIcon />
          <AlertDescription className="flex flex-row items-center justify-between gap-3 text-tag-blue-foreground">
            <span className="text-sm">
              GIFT onboarding needs more information about the nominee. Add
              their ID, phone and email in the account center.
            </span>
            <Button
              variant="outline"
              className="shrink-0"
              onClick={openNomineeEdit}
            >
              Update nominee
              <ExternalLinkIcon className="size-4" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {selectedClient && (
        <div className="mt-auto pt-6">
          <Button
            size="lg"
            onClick={handleContinue}
            disabled={isLoadingNominees || !!nomineeWithMissingInfo}
          >
            Continue
            <ArrowRightIcon className="size-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
